import { useEffect, useState } from 'react';

import {
  App,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { KitCampaign } from 'models/KitCampaign';
import { Role } from 'models/Role';
import { TiendaPerfectaDistributor } from 'models/TiendaPerfecta';

import { RoleService } from 'services/RoleService';

import { RoleFormPayload } from '../types';
import KitCampaignsFields from './KitCampaignsFields';
import TiendaPerfectaAccessFields, {
  EMPTY_TIENDA_PERFECTA_ACCESS,
  TiendaPerfectaAccessValue,
  buildTiendaPerfectaPayload,
} from './TiendaPerfectaAccessFields';

interface DetailRoleModalProps {
  isOpen: boolean;
  id: number;
  handleModalCancel: () => void;
  submit: (id: number, payload: RoleFormPayload) => void;
  campaigns: KitCampaign[];
  kitIntegrationEnabled: boolean;
  currentCampaignIds: number[];
  tiendaPerfectaIntegrationEnabled: boolean;
  tiendaPerfectaDistributors: TiendaPerfectaDistributor[];
  tiendaPerfectaDistributorsLoading: boolean;
}

function DetailRoleModal({
  isOpen,
  handleModalCancel,
  submit,
  id,
  campaigns,
  kitIntegrationEnabled,
  currentCampaignIds,
  tiendaPerfectaIntegrationEnabled,
  tiendaPerfectaDistributors,
  tiendaPerfectaDistributorsLoading,
}: DetailRoleModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [kitCampaignsFullAccess, setKitCampaignsFullAccess] = useState(false);
  const [tiendaPerfectaAccess, setTiendaPerfectaAccess] =
    useState<TiendaPerfectaAccessValue>(EMPTY_TIENDA_PERFECTA_ACCESS);
  const [knownDistributors, setKnownDistributors] = useState<
    TiendaPerfectaDistributor[]
  >([]);
  const { t } = useTranslation();

  useEffect(() => {
    form.resetFields();
    setKitCampaignsFullAccess(false);
    setTiendaPerfectaAccess(EMPTY_TIENDA_PERFECTA_ACCESS);
    setKnownDistributors([]);

    if (isOpen) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);

    const response = await RoleService.findById(id);

    if (response.success) {
      const data = response.data as Role;
      form.setFieldValue('name', data.name);
      form.setFieldValue('description', data.description || '');
      form.setFieldValue(
        'isDefaultRoleForStartedAccount',
        data.isDefaultRoleForStartedAccount
      );

      const fullAccess = data.kitCampaignsFullAccessEnabled ?? false;
      setKitCampaignsFullAccess(fullAccess);

      if (kitIntegrationEnabled && !fullAccess) {
        form.setFieldValue(
          'campaignIds',
          data.campaigns?.map((c) => c.id) ?? currentCampaignIds
        );
      }

      const tier = data.tiendaPerfectaDistributorTier ?? 'low';
      const hydratedDistributors = data.tiendaPerfectaDistributors ?? [];

      setKnownDistributors(hydratedDistributors);
      setTiendaPerfectaAccess({
        tier,
        // Con tier `top` la API enumera todos los distribuidores heredados:
        // son acceso, no una selección, y precargarlos sería engañoso.
        distributorIds:
          tier !== 'top' ? hydratedDistributors.map((d) => d.id) : [],
      });

      setLoading(false);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t(
            'scenes.settings.roles.components.detail_role_modal.errors.fetch_role_failed'
          ),
        3.5
      );
      handleModalCancel();
    }

    setLoading(false);
  };

  const handleFullAccessChange = (checked: boolean) => {
    setKitCampaignsFullAccess(checked);
    if (checked) {
      form.setFieldValue('campaignIds', []);
    }
  };

  const onFinish = async (values: any) => {
    setLoading(true);

    await submit(id, {
      name: values.name.trim(),
      description: (values.description || '').trim() || null,
      isDefaultRoleForStartedAccount:
        values.isDefaultRoleForStartedAccount === undefined
          ? false
          : values.isDefaultRoleForStartedAccount,
      kitCampaignsFullAccessEnabled: kitCampaignsFullAccess,
      campaignIds:
        kitIntegrationEnabled && !kitCampaignsFullAccess
          ? (values.campaignIds ?? []).filter(
              (v: number | string) => typeof v === 'number'
            )
          : [],
      ...buildTiendaPerfectaPayload(
        tiendaPerfectaAccess,
        tiendaPerfectaIntegrationEnabled
      ),
    });

    setLoading(false);
  };

  return (
    <Modal
      title={t('scenes.settings.roles.components.detail_role_modal.title', {
        id,
      })}
      open={isOpen}
      onCancel={handleModalCancel}
      maskClosable={false}
      destroyOnClose
      footer={null}
      width={560}
    >
      <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              name="name"
              label={t(
                'scenes.settings.roles.components.detail_role_modal.labels.name'
              )}
              rules={[
                {
                  required: true,
                  message: t(
                    'scenes.settings.roles.components.detail_role_modal.validations.name.required'
                  ),
                },
              ]}
            >
              <Input
                type="text"
                disabled={loading}
                placeholder={t(
                  'scenes.settings.roles.components.detail_role_modal.placeholders.name'
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              name="description"
              label={t(
                'scenes.settings.roles.components.detail_role_modal.labels.description'
              )}
            >
              <TextArea
                placeholder={t(
                  'scenes.settings.roles.components.detail_role_modal.placeholders.description'
                )}
                rows={4}
                disabled={loading}
              />
            </Form.Item>
          </Col>
        </Row>

        {kitIntegrationEnabled && (
          <KitCampaignsFields
            campaigns={campaigns}
            fullAccess={kitCampaignsFullAccess}
            onFullAccessChange={handleFullAccessChange}
            disabled={loading}
          />
        )}

        {tiendaPerfectaIntegrationEnabled && (
          <TiendaPerfectaAccessFields
            value={tiendaPerfectaAccess}
            onChange={setTiendaPerfectaAccess}
            distributors={tiendaPerfectaDistributors}
            distributorsLoading={tiendaPerfectaDistributorsLoading}
            disabled={loading}
            knownDistributors={knownDistributors}
          />
        )}

        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              name="isDefaultRoleForStartedAccount"
              valuePropName="checked"
            >
              <Checkbox disabled={loading}>
                {t(
                  'scenes.settings.roles.components.detail_role_modal.checkbox.default_role_text'
                )}
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24}>
            <Button
              onClick={() => {
                form.submit();
              }}
              loading={loading}
              type="primary"
              block
            >
              {t(
                'scenes.settings.roles.components.detail_role_modal.buttons.save_changes'
              )}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default DetailRoleModal;
